import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BORGHI } from '@/mocks/borghi';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

type Zona = 'tutti' | 'mare' | 'collina' | 'entroterra';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const BORGHI_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Borghi delle Marche — Scopri i borghi storici | Discovering Marche',
  description: 'Scopri tutti i borghi storici delle Marche: Osimo, Sirolo, Castelfidardo, Camerano, Fermo, Torre di Palme e molti altri. Storia, territorio e qualità della vita.',
  url: `${SITE_URL}/borghi`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Borghi', item: `${SITE_URL}/borghi` },
    ],
  },
};

const BorghiPage = () => {
  const { t, i18n } = useTranslation();
  const [zona, setZona] = useState<Zona>('tutti');
  const header = useScrollAnimation();
  const grid = useScrollAnimation({ threshold: 0.05 });

  const lang = i18n.language as 'it' | 'en' | 'de';

  const getTagline = (b: typeof BORGHI[0]) => {
    if (lang === 'en') return b.taglineEn;
    if (lang === 'de') return b.taglineDe;
    return b.tagline;
  };

  const filtered = zona === 'tutti' ? BORGHI : BORGHI.filter((b) => b.zona === zona);

  const zonaLabels: Record<Zona, string> = {
    tutti: t('borghi.filter.tutti'),
    mare: t('borghi.filter.mare'),
    collina: t('borghi.filter.collina'),
    entroterra: t('borghi.filter.entroterra'),
  };

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Borghi delle Marche — Scopri i borghi storici | Discovering Marche"
        description="Scopri i borghi storici delle Marche: Osimo, Sirolo, Castelfidardo, Camerano, Fermo, Torre di Palme. Storia, territorio, cultura e qualità della vita nei borghi medievali marchigiani."
        keywords="borghi Marche, borghi storici Marche, borghi medievali Marche, Osimo Sirolo Castelfidardo, vivere nei borghi Marche"
        canonical="/borghi"
        jsonLd={BORGHI_JSONLD}
      />
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20medieval%20borghi%20villages%20panoramic%20aerial%20view%20rolling%20hills%20terracotta%20rooftops%20stone%20buildings%20golden%20sunset%20beautiful%20Italian%20landscape%20countryside&width=1920&height=900&seq=borghiHero&orientation=landscape"
            alt="I borghi delle Marche — borghi storici Marche"
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-white mb-4">{t('borghi.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-2xl leading-tight">
            {t('borghi.title')}
          </h1>
        </div>
      </section>

      {/* Intro + Filtri */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div
          ref={header.ref}
          className={`fade-in-up ${header.isVisible ? 'visible' : ''} max-w-2xl mb-12`}
        >
          <p className="font-sans text-base text-anthracite-500 leading-relaxed">
            {t('borghi.intro')}
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-3 mb-12">
          {(Object.keys(zonaLabels) as Zona[]).map((z) => (
            <button
              key={z}
              onClick={() => setZona(z)}
              className={`px-5 py-2 rounded-full font-sans text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                zona === z
                  ? 'bg-terracotta-300 border-terracotta-300 text-white'
                  : 'border-anthracite-200 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300'
              }`}
            >
              {zonaLabels[z]}
            </button>
          ))}
        </div>

        {/* Grid — tutti i borghi visibili */}
        <div
          ref={grid.ref}
          className={`fade-in-up ${grid.isVisible ? 'visible' : ''} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}
        >
          {filtered.map((borgo, idx) => (
            <Link
              key={borgo.slug}
              to={`/borghi/${borgo.slug}`}
              className={`group block cursor-pointer ${idx === 1 ? 'stagger-2' : idx === 2 ? 'stagger-3' : ''}`}
            >
              <article>
                <div className="relative overflow-hidden rounded-xl aspect-[4/3] mb-5">
                  {['osimo', 'camerano', 'castelfidardo', 'varano', 'sirolo', 'campocavallo', 'staffolo', 'san-severino', 'tolentino', 'fiastra', 'torre-di-palme', 'offagna', 'altidona', 'montelupone', 'fermo', 'petritoli', 'monte-sicuro', 'servigliano', 'montefano', 'sarnano', 'numana'].includes(borgo.slug) ? (
                    <img
                      src={borgo.image}
                      alt={`${borgo.name} — borgo storico Marche`}
                      title={`${borgo.name} — immobili borghi Marche`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#C8956D]" />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-400"></div>
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block font-sans text-xs tracking-widest uppercase px-3 py-1.5 rounded-full text-white ${
                      borgo.zona === 'mare' ? 'bg-sky-600/80' :
                      borgo.zona === 'collina' ? 'bg-olive-600/80' :
                      'bg-anthracite-600/80'
                    }`}>
                      {t(`borghi.zona.${borgo.zona}`)}
                    </span>
                  </div>
                </div>
                <div className="px-1">
                  <div className="flex items-baseline gap-2 mb-2">
                    <h2 className="font-serif text-2xl text-anthracite-800 font-medium group-hover:text-terracotta-300 transition-colors">
                      {borgo.name}
                    </h2>
                    <span className="font-sans text-xs text-anthracite-400">({borgo.provincia})</span>
                  </div>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    {getTagline(borgo)}
                  </p>
                  <span className="inline-flex items-center gap-2 font-sans text-sm text-terracotta-300 group-hover:gap-3 transition-all duration-300 whitespace-nowrap">
                    {t('borghi.scopri')}
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </section>



      {/* CTA */}
      <section className="bg-ivory-100 py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">{t('borghi.cta.overline')}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6">
            {t('borghi.cta.title')}
          </h2>
          <p className="font-sans text-sm text-anthracite-500 mb-8 leading-relaxed">
            {t('borghi.cta.text')}
          </p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t('borghi.cta.btn')}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default BorghiPage;
